// externalData — the /data/external backend-visualization surface's data layer.
//
// Owns the FIVE functions the prebuilt shared Remote* components call via
// el.call (parseRemotePath / makeRemoteUrl / schemaForRemoteTable /
// saveRemoteRow / deleteRemoteRow — see shared/components/RemoteSchemaList.js,
// RemoteTableCard.js, RemoteRowTable.js, RemoteRowEditor.js) plus the page's
// loaders (sources, schema, rows), the selection/URL sync, the custom_http
// request runner, and the fetch-wiring snippet builders.
//
// Selection model — STATE-DRIVEN, single registered route:
//   root._extPath holds the canonical '/data/external[/<slug>[/<table>]]'
//   string. The records plane rides the reserved pseudo-slug 'workspace'
//   (one "table" per RecordCollection), so every Remote* component works
//   unchanged for both org integrations and workspace records.
//   Only '/data/external' is a registered route, so NAVIGATION carries the
//   selection as query params ('/data/external?src=<slug>&table=<table>') —
//   a path-segment URL would fall through to the '/*' projectRedirect
//   catch-all. parseRemotePath accepts BOTH shapes (path segments + query)
//   for resilience; makeRemoteUrl emits the routable query shape.
//
// Backend routing (saveRemoteRow / deleteRemoteRow / extLoadRows):
//   slug 'workspace'      → sdk.execute('workspaceProject.records', …)
//   slug = integration    → sdk.execute('orgIntegration', 'call', …) via
//                           callCapability — instantdb only (its capability
//                           strings ARE the ops: list/get/create/update/
//                           remove, namespace-allowlisted server-side).
//   Everything else is read-only here (postgres/mongodb/neon/planetscale/
//   firebase declare data capabilities but the dispatcher is not implemented
//   yet — the page renders "query engine coming soon" for those).
//
// SDK-only transport throughout — no raw fetch anywhere.

import _wsShared from '@symbo-ls/workspace-shared'
import { shellStripWorkspacePrefix } from './shellSharedDelegates.js'
const shellListIntegrationKinds = (...args) => _wsShared.functions.listIntegrationKinds(...args)
const shellListScopedIntegrations = (...args) => _wsShared.functions.listScopedIntegrations(...args)
const shellCallCapability = (...args) => _wsShared.functions.callCapability(...args)
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
import { activeWorkspaceId } from './scopedClient.js'

export const EXT_BASE_PATH = '/data/external'
// Reserved pseudo-slug for the workspace records plane. A RecordCollection
// renders as a "table" under this slug so the Remote* components (which think
// in slug/table pairs) drive both backends with one contract.
export const EXT_WORKSPACE_SLUG = 'workspace'

const RECORDS_PAGE_SIZE = 100 // records plane 400s on limit > 100 — never raise
const RECORDS_MAX_ROWS = 2000
const INSTANTDB_LIST_LIMIT = 500 // server MAX_LIMIT is 1000

// Base columns every records-plane row carries (WorkspaceRecord wire shape:
// { id, collection, name, data, created_at, updated_at } — custom fields live
// in the `data` bag and are flattened onto the browsing row).
const RECORD_BASE_COLUMNS = [
  { name: 'id', type: 'string', format: 'uuid', is_primary_key: true, nullable: false },
  { name: 'name', type: 'string', nullable: true }
]
const RECORD_TAIL_COLUMNS = [
  { name: 'created_at', type: 'string', format: 'date-time', nullable: true },
  { name: 'updated_at', type: 'string', format: 'date-time', nullable: true }
]
const RECORD_META_KEYS = ['id', '_id', 'name', 'collection', 'created_at', 'updated_at', '__raw']

// ---------- small resolvers ----------

const _externalRootFrom = (el, maybeRoot) => {
  if (maybeRoot && typeof maybeRoot.update === 'function') {
    return maybeRoot.root && typeof maybeRoot.root.update === 'function'
      ? maybeRoot.root
      : maybeRoot
  }
  if (el && typeof el.getRootState === 'function') return el.getRootState()
  return null
}

const _elFrom = (self) => (self && typeof self.call === 'function' ? self : null)

// A refusal the Remote row editor prints as its toast message
// (RemoteRowEditor passes `res.error` as `message`): through the bound
// element's polyglot, or the shipped English when none is bound — never a
// raw key.
const _extRefusal = (el, key) => ({
  ok: false,
  error: el
    ? String(el.call('polyglot', key))
    : String(_wsShared.functions.i18nStaticEnglish()[key] || key)
})

const _orgId = (root) =>
  root?.jwtClaims?.workspace_org_id || root?.org?.id || root?.org?._id || null

const _integrationRows = (root) =>
  Array.isArray(root?._extIntegrations) ? root._extIntegrations : []

const _findIntegration = (root, slug) =>
  _integrationRows(root).find((r) => r && r.slug === slug) || null

const _capabilityRef = (root, row) => ({
  orgId: _orgId(root),
  kind: row.kind,
  slug: row.slug || 'default',
  scopeType: row.scope_type || 'org',
  scopeId: row.scope_id ?? null,
  workspaceId: activeWorkspaceId(root) || undefined
})

// Translate a callCapability outcome into a human-readable error line (or
// null when the call succeeded). Permission failures surface as inline
// notices — never crashes.
const _readableCallError = (el, res) => {
  const t = (k) => el.call('polyglot', k)
  if (!res) return t('data.external.error.noResponse')
  if (res.error) {
    if (res.status === 401) return t('data.external.error.notSignedIn')
    if (res.status === 403) return t('data.external.error.notAllowed')
    if (res.status === 404) return t('data.external.error.notFound')
    return res.status
      ? t('data.external.error.httpStatus')
          .replace('{error}', res.error)
          .replace('{status}', res.status)
      : res.error
  }
  if (res.ok === false) {
    const detail = typeof res.result === 'string' ? res.result.slice(0, 160) : ''
    const status = res.status || t('data.external.error.unknownStatus')
    return detail
      ? t('data.external.error.upstreamWithDetail')
          .replace('{status}', status)
          .replace('{detail}', detail)
      : t('data.external.error.upstream').replace('{status}', status)
  }
  return null
}

// ---------- canonical path model ----------

const _canonicalPath = (slug, table) => {
  if (!slug) return EXT_BASE_PATH
  const s = encodeURIComponent(String(slug))
  if (!table) return `${EXT_BASE_PATH}/${s}`
  return `${EXT_BASE_PATH}/${s}/${encodeURIComponent(String(table))}`
}

const _parsePathString = (raw) => {
  const out = { base: EXT_BASE_PATH, slug: null, table: null }
  if (!raw || typeof raw !== 'string') return out
  let str = raw
  let query = ''
  const hashIdx = str.indexOf('#')
  if (hashIdx >= 0) str = str.slice(0, hashIdx)
  const qIdx = str.indexOf('?')
  if (qIdx >= 0) {
    query = str.slice(qIdx + 1)
    str = str.slice(0, qIdx)
  }
  str = shellStripWorkspacePrefix(str || '')
  // Routable query shape: /data/external?src=<slug>&table=<table>
  if (query) {
    const params = new URLSearchParams(query)
    const src = params.get('src')
    if (src) {
      out.slug = src
      out.table = params.get('table') || null
      return out
    }
  }
  // Path-segment shape: /data/external[/<slug>[/<table>]]
  if (str === EXT_BASE_PATH || str.indexOf(`${EXT_BASE_PATH}/`) === 0) {
    const rest = str.slice(EXT_BASE_PATH.length).split('/').filter(Boolean)
    if (rest[0]) out.slug = decodeURIComponent(rest[0])
    if (rest[1]) out.table = decodeURIComponent(rest[1])
  }
  return out
}

// ---------- THE FIVE Remote* contract functions ----------

// parseRemotePath([path]) → { base, slug, table } — never null.
// Zero-arg form (how every Remote* component calls it) reads the live
// selection off root._extPath; a string arg parses that string instead
// (both '/data/external/<slug>/<table>' and '?src=&table=' shapes).
export const parseRemotePath = function parseRemotePath(path) {
  if (typeof path === 'string' && path) return _parsePathString(path)
  const el = _elFrom(this)
  const root = _externalRootFrom(el, null)
  if (root && typeof root._extPath === 'string' && root._extPath) {
    return _parsePathString(root._extPath)
  }
  if (typeof window !== 'undefined') {
    return _parsePathString(window.location.pathname + window.location.search)
  }
  return { base: EXT_BASE_PATH, slug: null, table: null }
}

// makeRemoteUrl(base, { slug, table }) → routable URL string. Only the base
// path is a registered route, so the selection rides query params — a
// path-segment URL would miss the route map and fall through to the '/*'
// projectRedirect catch-all.
export const makeRemoteUrl = function makeRemoteUrl(base, sel = {}) {
  let b = typeof base === 'string' && base ? base : EXT_BASE_PATH
  b = b.split(/[?#]/)[0].replace(/\/+$/, '') || EXT_BASE_PATH
  const slug = sel && sel.slug ? String(sel.slug) : null
  if (!slug) return b
  const params = new URLSearchParams()
  params.set('src', slug)
  if (sel.table) params.set('table', String(sel.table))
  return `${b}?${params.toString()}`
}

// schemaForRemoteTable(root, slug, table) → { name, columns, … } | null.
// Reads root._extSchemaBySlug. Tolerates the (slug, table) arg shape (root
// resolved off `this`) so ad-hoc callers can't misfire.
export const schemaForRemoteTable = function schemaForRemoteTable(rootArg, slugArg, tableArg) {
  let root = rootArg
  let slug = slugArg
  let table = tableArg
  if (typeof rootArg === 'string') {
    table = slugArg
    slug = rootArg
    root = null
  }
  if (!root || typeof root !== 'object') root = _externalRootFrom(_elFrom(this), null)
  if (!root || !slug || !table) return null
  const tables = root._extSchemaBySlug ? root._extSchemaBySlug[slug] : null
  if (!Array.isArray(tables)) return null
  return tables.find((t) => t && t.name === table) || null
}

// Editor string values → typed payload, per the column schema. '' means null
// for nullable columns; booleans come from the editor's true/false select;
// json textareas must parse.
const _coerceValues = (el, schema, values, mode) => {
  const out = {}
  const cols = schema?.columns || []
  const byName = {}
  for (const c of cols) byName[c.name] = c
  for (const key in values || {}) {
    const col = byName[key]
    const raw = values[key]
    if (!col) {
      if (raw !== '' && raw != null) out[key] = raw
      continue
    }
    if (col.is_primary_key && mode === 'update') continue // PK immutable
    if (raw === '' || raw == null) {
      if (col.is_primary_key) continue // let the backend generate ids
      out[key] = null
      continue
    }
    if (col.type === 'boolean') {
      out[key] = raw === true || raw === 'true'
    } else if (col.type === 'number' || col.type === 'integer') {
      const n = Number(raw)
      if (Number.isNaN(n)) {
        return {
          error: el
            .call('polyglot', 'data.external.error.notANumber')
            .replace('{field}', key)
            .replace('{value}', raw)
        }
      }
      out[key] = n
    } else if (col.format === 'json' || col.format === 'jsonb') {
      if (typeof raw === 'string') {
        try {
          out[key] = JSON.parse(raw)
        } catch (e) {
          return {
            error: el
              .call('polyglot', 'data.external.error.invalidJson')
              .replace('{field}', key)
              .replace('{message}', e.message)
          }
        }
      } else {
        out[key] = raw
      }
    } else {
      out[key] = raw
    }
  }
  return { values: out }
}

const _saveRecordRow = async (el, root, ed, values) => {
  const sdk = shellGetSDK()
  const workspaceId = activeWorkspaceId(root)
  const data = {}
  let name
  for (const key in values) {
    if (key === 'name') name = values[key]
    else if (!RECORD_META_KEYS.includes(key)) data[key] = values[key]
  }
  if (ed.mode === 'update') {
    const id = ed.original?.id || ed.original?._id
    if (!id) return _extRefusal(el, 'data.external.error.rowIdMissing')
    // PATCH replaces the fields it carries — merge over the original data
    // bag so untouched custom fields survive the write.
    const baseData = (ed.original && ed.original.__raw && ed.original.__raw.data) || {}
    await sdk.execute('workspaceProject.records', 'update', {
      id,
      collection: ed.table,
      ...(name !== undefined ? { name } : {}),
      data: { ...baseData, ...data },
      ...(workspaceId ? { workspaceId } : {})
    })
  } else {
    await sdk.execute('workspaceProject.records', 'create', {
      collection: ed.table,
      ...(name !== undefined ? { name } : {}),
      data,
      ...(workspaceId ? { workspaceId } : {})
    })
  }
  return { ok: true }
}

const _saveInstantDbRow = async (el, root, ed, values) => {
  const row = _findIntegration(root, ed.slug)
  if (!row) {
    return {
      ok: false,
      error: el
        .call('polyglot', 'data.external.error.integrationNotFound')
        .replace('{slug}', ed.slug)
    }
  }
  if (row.kind !== 'instantdb') {
    return {
      ok: false,
      error: el
        .call('polyglot', 'data.external.error.readOnly')
        .replace('{kind}', row.kindLabel || row.kind)
    }
  }
  const isUpdate = ed.mode === 'update'
  const id = isUpdate ? ed.original?.id || ed.original?._id : values.id || undefined
  if (isUpdate && !id) return _extRefusal(el, 'data.external.error.rowIdMissing')
  const vals = { ...values }
  delete vals.id
  const res = await shellCallCapability(_capabilityRef(root, row), isUpdate ? 'update' : 'create', {
    namespace: ed.table,
    ...(id ? { id } : {}),
    values: vals
  })
  const err = _readableCallError(el, res)
  return err ? { ok: false, error: err } : { ok: true }
}

// saveRemoteRow(root) → { ok } | { ok: false, error }. Reads root._extEditor
// ({ mode, slug, table, values, original } — RemoteRowEditor's contract),
// coerces values against the table schema, routes by source kind, refreshes
// the rows on success and closes the editor.
export const saveRemoteRow = async function saveRemoteRow(rootArg) {
  const el = _elFrom(this)
  const root = _externalRootFrom(el, rootArg)
  if (!root) return _extRefusal(el, 'data.external.error.noRoot')
  const ed = root._extEditor
  if (!ed || !ed.slug || !ed.table) return _extRefusal(el, 'data.external.error.nothingToSave')
  if (root._extEditorSaving) return _extRefusal(el, 'data.external.error.saveInFlight')
  const schema = schemaForRemoteTable(root, ed.slug, ed.table)
  const coerced = _coerceValues(el, schema, ed.values, ed.mode)
  if (coerced.error) return { ok: false, error: coerced.error }
  root.update({ _extEditorSaving: true })
  try {
    const res =
      ed.slug === EXT_WORKSPACE_SLUG
        ? await _saveRecordRow(el, root, ed, coerced.values)
        : await _saveInstantDbRow(el, root, ed, coerced.values)
    if (!res.ok) {
      root.update({ _extEditorSaving: false })
      return res
    }
    root.update({ _extEditorSaving: false, _extEditor: null })
    await extLoadRows.call(el, root, { force: true })
    return { ok: true }
  } catch (e) {
    root.update({ _extEditorSaving: false })
    return { ok: false, error: e?.message || String(e) }
  }
}

// deleteRemoteRow(root) → { ok } | { ok: false, error }. Update-mode only —
// deletes root._extEditor.original from the selected backend, refreshes the
// rows and closes the editor.
export const deleteRemoteRow = async function deleteRemoteRow(rootArg) {
  const el = _elFrom(this)
  const root = _externalRootFrom(el, rootArg)
  if (!root) return _extRefusal(el, 'data.external.error.noRoot')
  const ed = root._extEditor
  if (!ed || ed.mode !== 'update') return _extRefusal(el, 'data.external.error.nothingToDelete')
  const id = ed.original?.id || ed.original?._id
  if (!id) return _extRefusal(el, 'data.external.error.rowIdMissing')
  try {
    if (ed.slug === EXT_WORKSPACE_SLUG) {
      const workspaceId = activeWorkspaceId(root)
      await shellGetSDK().execute('workspaceProject.records', 'remove', {
        id,
        ...(workspaceId ? { workspaceId } : {})
      })
    } else {
      const row = _findIntegration(root, ed.slug)
      if (!row) {
        return {
          ok: false,
          error: el
            .call('polyglot', 'data.external.error.integrationNotFound')
            .replace('{slug}', ed.slug)
        }
      }
      if (row.kind !== 'instantdb') {
        return {
          ok: false,
          error: el
            .call('polyglot', 'data.external.error.readOnly')
            .replace('{kind}', row.kindLabel || row.kind)
        }
      }
      const res = await shellCallCapability(_capabilityRef(root, row), 'remove', {
        namespace: ed.table,
        id
      })
      const err = _readableCallError(el, res)
      if (err) return { ok: false, error: err }
    }
    root.update({ _extEditor: null })
    await extLoadRows.call(el, root, { force: true })
    return { ok: true }
  } catch (e) {
    return { ok: false, error: e?.message || String(e) }
  }
}

// ---------- root-state seed ----------

// v3.14 factories track only the state keys present at first render — seed
// every `_ext*` key BEFORE the page's children mount (call from onInit).
export const extEnsureStateSeed = function extEnsureStateSeed(rootArg) {
  const root = _externalRootFrom(_elFrom(this), rootArg)
  if (!root) return
  const defaults = {
    _extPath: EXT_BASE_PATH,
    _extIntegrations: [],
    _extCollections: [],
    _extSourcesLoading: false,
    _extSourcesLoaded: false,
    _extSourcesError: null,
    _extSchemaBySlug: {},
    _extSchemaLoading: false,
    _extSchemaError: null,
    _extRows: [],
    _extRowsLoading: false,
    _extRowsError: null,
    _extRowsKey: null,
    _extRowsTotal: null,
    _extRowsHasMore: false,
    _extRowCounts: {},
    _extEditor: null,
    _extEditorSaving: false,
    _extRunner: {
      method: 'GET',
      path: '/',
      body: '',
      running: false,
      response: null,
      status: null,
      error: null
    }
  }
  const patch = {}
  for (const key in defaults) {
    if (root[key] === undefined) patch[key] = defaults[key]
  }
  if (Object.keys(patch).length) root.update(patch)
}

// ---------- sources (rail) ----------

const _fieldTypeToColumn = (def) => {
  const t = String(def?.type || '').toLowerCase()
  const col = { name: def.key, type: 'string', nullable: !def.required }
  if (def.label) col.label = def.label
  if (t === 'boolean' || t === 'checkbox' || t === 'toggle') col.type = 'boolean'
  else if (t === 'number' || t === 'integer' || t === 'currency' || t === 'float') {
    col.type = 'number'
  } else if (t === 'json' || t === 'object' || t === 'array') {
    col.format = 'json'
  } else if (t === 'date' || t === 'datetime' || t === 'date-time') {
    col.format = 'date-time'
  } else if (t === 'select' || t === 'multiselect') {
    col.format = t
  } else if (t) {
    col.format = t
  }
  return col
}

const _recordTables = (collections, fieldDefs) => {
  const defsByEntity = {}
  for (const def of fieldDefs || []) {
    if (!def || !def.key) continue
    const entity = String(def.entityType || '')
    // CustomFieldDef.entityType for record collections is 'record:<key>' —
    // tolerate a bare key too so older rows still resolve.
    const key = entity.indexOf('record:') === 0 ? entity.slice('record:'.length) : entity
    if (!defsByEntity[key]) defsByEntity[key] = []
    defsByEntity[key].push(def)
  }
  return (collections || []).map((c) => ({
    name: c.key,
    label: c.label || c.key,
    icon: c.icon || null,
    color: c.color || null,
    titleField: c.titleField || null,
    columns: [
      ...RECORD_BASE_COLUMNS,
      ...(defsByEntity[c.key] || [])
        .slice()
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map(_fieldTypeToColumn),
      ...RECORD_TAIL_COLUMNS
    ],
    ops: ['list', 'get', 'create', 'update', 'remove']
  }))
}

// config.namespaceAllowlist arrives as an object ({ namespace: [ops] }) or a
// JSON string typed into the admin form — accept both, fail to {}.
const _namespaceAllowlist = (config) => {
  const raw = config?.namespaceAllowlist
  if (!raw) return {}
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch {
      return {}
    }
  }
  return {}
}

const _instantDbTables = (row) =>
  Object.keys(_namespaceAllowlist(row.config)).map((namespace) => ({
    name: namespace,
    columns: [RECORD_BASE_COLUMNS[0]], // id only until rows reveal real keys
    columnsDerived: false,
    ops: (_namespaceAllowlist(row.config)[namespace] || []).map(String)
  }))

// Kinds that DECLARE data capabilities but whose dispatcher isn't implemented
// yet — surfaced as "query engine coming soon", never a dead Run button.
export const extHasDataCapabilities = (row) =>
  (row?.capabilities || []).some(
    (c) => c === 'data.read' || c === 'data.write' || c === 'schema.introspect'
  )

// Load both rail groups: org/workspace integrations (+ kind metadata) and
// the workspace RecordCollections (+ their CustomFieldDefs → column schemas).
// Retries via a claims watcher when jwtClaims haven't resolved yet (cold
// load), mirroring /admin/integrations.
export const extLoadSources = async function extLoadSources(rootArg, opts = {}) {
  const el = _elFrom(this)
  const root = _externalRootFrom(el, rootArg)
  if (!root) return
  const orgId = _orgId(root)
  if (!orgId) {
    if (!root._extClaimsWatcher && typeof setInterval === 'function') {
      root._extClaimsWatcher = setInterval(() => {
        if (_orgId(root)) {
          clearInterval(root._extClaimsWatcher)
          root._extClaimsWatcher = null
          extLoadSources.call(el, root, opts)
        }
      }, 300)
    }
    return
  }
  if (root._extSourcesLoading && !opts.force) return
  root.update({ _extSourcesLoading: true, _extSourcesError: null })
  try {
    const workspaceId = activeWorkspaceId(root)
    const sdk = shellGetSDK()
    // Partial-failure tolerant: a records-plane permission error must not
    // blank the integrations rail (and vice versa) — whichever group loads
    // renders; the first failure surfaces as the inline notice.
    const settled = await Promise.allSettled([
      shellListIntegrationKinds(),
      shellListScopedIntegrations(
        workspaceId
          ? { type: 'workspace', orgId, id: workspaceId, includeParents: true }
          : { type: 'org', orgId }
      ),
      sdk.execute('recordCollections', 'list', workspaceId ? { workspaceId } : {}),
      sdk.execute('fieldDefs', 'list', workspaceId ? { workspaceId } : {})
    ])
    const [kinds, rows, collections, fieldDefs] = settled.map((r) =>
      r.status === 'fulfilled' ? r.value : null
    )
    const firstFailure = settled.find((r) => r.status === 'rejected')
    const partialError = firstFailure
      ? firstFailure.reason?.message || String(firstFailure.reason)
      : null
    const kindByName = {}
    for (const k of kinds || []) kindByName[k.kind] = k
    const integrations = (Array.isArray(rows) ? rows : [])
      .filter((r) => r && r.enabled !== false)
      .map((r) => {
        const kind = kindByName[r.kind] || {}
        return {
          slug: r.slug || 'default',
          kind: r.kind,
          kindLabel: kind.label || r.kind,
          kindIcon: kind.icon || null,
          category: kind.category || null,
          capabilities: kind.capabilities || [],
          display_name: r.display_name || kind.label || r.kind,
          scope_type: r.scope_type || 'org',
          scope_id: r.scope_id ?? null,
          dispatchable: !!r.dispatchable,
          has_secret: !!r.has_secret,
          config: r.config || {}
        }
      })
    const cols = Array.isArray(collections) ? collections : []
    const defs = Array.isArray(fieldDefs) ? fieldDefs : []
    const schemaBySlug = { [EXT_WORKSPACE_SLUG]: _recordTables(cols, defs) }
    for (const row of integrations) {
      if (row.kind === 'instantdb') schemaBySlug[row.slug] = _instantDbTables(row)
      else if (!schemaBySlug[row.slug]) schemaBySlug[row.slug] = []
    }
    root.update({
      _extIntegrations: integrations,
      _extCollections: cols.map((c) => ({
        key: c.key,
        label: c.label || c.key,
        labelSingular: c.labelSingular || null,
        icon: c.icon || null,
        color: c.color || null,
        titleField: c.titleField || null
      })),
      _extSchemaBySlug: schemaBySlug,
      _extSourcesLoading: false,
      _extSourcesLoaded: true,
      _extSourcesError: partialError
    })
    extLoadRowCounts.call(el, root)
    // Cold deep-link: the selection may have been synced (and its row load
    // errored with "integration not found") BEFORE the source registry
    // resolved — re-run the row load now that integrations are known.
    const { slug: selSlug } = _parsePathString(root._extPath || '')
    if (selSlug && selSlug !== EXT_WORKSPACE_SLUG) {
      extLoadRows.call(el, root, { force: true })
    }
  } catch (e) {
    root.update({
      _extSourcesLoading: false,
      _extSourcesLoaded: true,
      _extSourcesError: e?.message || String(e)
    })
  }
}

// Lazy per-collection row counts for the rail — one limit-1 page per
// collection, sequential (small N), read off the pagination envelope.
export const extLoadRowCounts = async function extLoadRowCounts(rootArg) {
  const root = _externalRootFrom(_elFrom(this), rootArg)
  if (!root) return
  const collections = Array.isArray(root._extCollections) ? root._extCollections : []
  if (!collections.length) return
  const sdk = shellGetSDK()
  const workspaceId = activeWorkspaceId(root)
  for (const c of collections) {
    try {
      const page = await sdk.execute('workspaceProject.records', 'list', {
        collection: c.key,
        limit: 1,
        ...(workspaceId ? { workspaceId } : {})
      })
      const total = page?.pagination?.totalCount
      if (typeof total === 'number') {
        root.update({ _extRowCounts: { ...(root._extRowCounts || {}), [c.key]: total } })
      }
    } catch {
      // count stays absent — the rail simply shows no badge
    }
  }
}

// ---------- rows ----------

const _flattenRecordRow = (r) => {
  const flat = {
    id: r.id || r._id,
    name: r.name ?? null,
    ...(r.data && typeof r.data === 'object' ? r.data : {}),
    created_at: r.created_at || null,
    updated_at: r.updated_at || null
  }
  flat.__raw = r
  return flat
}

// Records-plane pager — id.asc + repeat-first-row guard + dedupe (offset
// paging over a non-unique order silently loses rows; see the commerce
// comListAllRecords discipline this mirrors).
const _loadRecordRows = async (root, table) => {
  const sdk = shellGetSDK()
  const workspaceId = activeWorkspaceId(root)
  const rows = []
  const seen = {}
  let lastFirstId = null
  let total = null
  let hasMore = false
  for (let offset = 0; offset < RECORDS_MAX_ROWS; offset += RECORDS_PAGE_SIZE) {
    const page = await sdk.execute('workspaceProject.records', 'list', {
      collection: table,
      limit: RECORDS_PAGE_SIZE,
      offset,
      order: 'id.asc',
      ...(workspaceId ? { workspaceId } : {})
    })
    const arr = Array.isArray(page) ? page : []
    if (page?.pagination && typeof page.pagination.totalCount === 'number') {
      total = page.pagination.totalCount
    }
    const firstId = arr[0] && (arr[0].id || arr[0]._id)
    if (firstId && firstId === lastFirstId) break
    lastFirstId = firstId || lastFirstId
    for (const r of arr) {
      const id = r && (r.id || r._id)
      if (id == null || seen[id]) continue
      seen[id] = true
      rows.push(_flattenRecordRow(r))
    }
    if (arr.length < RECORDS_PAGE_SIZE) break
    if (rows.length >= RECORDS_MAX_ROWS) {
      hasMore = true
      break
    }
  }
  return { rows, total: total ?? rows.length, hasMore }
}

// Union the keys of fetched rows into a column schema (instantdb namespaces
// are not introspectable — the allowlist names them, the rows reveal shape).
const _deriveColumns = (rows) => {
  const names = []
  const seen = {}
  const push = (n) => {
    if (!n || seen[n]) return
    seen[n] = true
    names.push(n)
  }
  push('id')
  for (const r of rows) {
    if (r && typeof r === 'object') for (const k in r) push(k)
  }
  return names.map((name) => {
    const sample = rows.find((r) => r && r[name] !== null && r[name] !== undefined)
    const v = sample ? sample[name] : null
    const col = { name, type: 'string', nullable: name !== 'id' }
    if (name === 'id') {
      col.is_primary_key = true
      col.format = 'uuid'
    } else if (typeof v === 'boolean') col.type = 'boolean'
    else if (typeof v === 'number') col.type = 'number'
    else if (v && typeof v === 'object') {
      col.type = 'object'
      col.format = 'json'
    }
    return col
  })
}

const _mergeDerivedColumns = (root, slug, table, rows) => {
  const tables = (root._extSchemaBySlug && root._extSchemaBySlug[slug]) || []
  const next = tables.map((t) =>
    t && t.name === table && rows.length
      ? { ...t, columns: _deriveColumns(rows), columnsDerived: true }
      : t
  )
  root.update({ _extSchemaBySlug: { ...(root._extSchemaBySlug || {}), [slug]: next } })
}

const _loadInstantDbRows = async (el, root, slug, table) => {
  const row = _findIntegration(root, slug)
  if (!row) {
    return {
      error: el.call('polyglot', 'data.external.error.integrationNotFound').replace('{slug}', slug)
    }
  }
  const res = await shellCallCapability(_capabilityRef(root, row), 'list', {
    namespace: table,
    limit: INSTANTDB_LIST_LIMIT
  })
  const err = _readableCallError(el, res)
  if (err) return { error: err }
  const rows = Array.isArray(res?.result?.rows) ? res.result.rows : []
  const total = typeof res?.result?.total === 'number' ? res.result.total : rows.length
  _mergeDerivedColumns(root, slug, table, rows)
  return { rows, total, hasMore: rows.length >= INSTANTDB_LIST_LIMIT }
}

// Load the rows for the CURRENT selection (root._extPath) into root._extRows.
// Stale-response safe: a selection change mid-flight drops the late result.
export const extLoadRows = async function extLoadRows(rootArg, opts = {}) {
  const el = _elFrom(this)
  const root = _externalRootFrom(el, rootArg)
  if (!root) return
  const { slug, table } = _parsePathString(root._extPath || '')
  if (!slug || !table) {
    root.update({
      _extRows: [],
      _extRowsLoading: false,
      _extRowsError: null,
      _extRowsKey: null,
      _extRowsTotal: null,
      _extRowsHasMore: false
    })
    return
  }
  const key = `${slug}/${table}`
  if (root._extRowsLoading && root._extRowsKey === key && !opts.force) return
  root.update({ _extRowsLoading: true, _extRowsError: null, _extRowsKey: key })
  try {
    let out
    if (slug === EXT_WORKSPACE_SLUG) {
      out = await _loadRecordRows(root, table)
    } else {
      const row = _findIntegration(root, slug)
      if (!row) {
        out = {
          error: el
            .call('polyglot', 'data.external.error.integrationNotFound')
            .replace('{slug}', slug)
        }
      } else if (row.kind === 'instantdb' && row.dispatchable) {
        out = await _loadInstantDbRows(el, root, slug, table)
      } else {
        out = {
          error: el
            .call('polyglot', 'data.external.error.notBrowsable')
            .replace('{kind}', row.kindLabel || row.kind)
        }
      }
    }
    if (root._extRowsKey !== key) return // selection moved on — drop stale page
    if (out.error) {
      root.update({ _extRows: [], _extRowsLoading: false, _extRowsError: out.error })
      return
    }
    root.update({
      _extRows: out.rows,
      _extRowsLoading: false,
      _extRowsError: null,
      _extRowsTotal: out.total,
      _extRowsHasMore: !!out.hasMore
    })
  } catch (e) {
    if (root._extRowsKey !== key) return
    root.update({
      _extRows: [],
      _extRowsLoading: false,
      _extRowsError: e?.message || String(e)
    })
  }
}

// ---------- selection / URL sync ----------

// Rail + card clicks route through the router (shareable URLs, back/forward
// works); the page's onUpdate hook then funnels into extSyncFromUrl.
export const extSelectSource = function extSelectSource(sel = {}) {
  const el = _elFrom(this)
  if (!el) return
  const url = makeRemoteUrl(EXT_BASE_PATH, sel)
  const root = el.call('findRouteRoot', el) || el.getRoot()
  if (root) el.router(url, root)
}

// Parse the live URL into root._extPath and (re)load what the selection
// needs. Called from the page's onCreate + onUpdate (currentPath/currentSearch
// are synced reactively by installAppShellSync).
export const extSyncFromUrl = function extSyncFromUrl(rootArg) {
  const el = _elFrom(this)
  const root = _externalRootFrom(el, rootArg)
  if (!root) return
  const pathname = typeof window !== 'undefined' ? window.location.pathname : root.currentPath || ''
  const search = typeof window !== 'undefined' ? window.location.search : root.currentSearch || ''
  if (shellStripWorkspacePrefix(pathname) !== EXT_BASE_PATH) return
  const { slug, table } = _parsePathString(pathname + search)
  const canonical = _canonicalPath(slug, table)
  if (root._extPath === canonical) return
  root.update({
    _extPath: canonical,
    _extEditor: null,
    _extRows: [],
    _extRowsError: null,
    _extRowsTotal: null,
    _extRowsHasMore: false
  })
  extLoadRows.call(el, root)
}

// '+ New row' — open the shared RemoteRowEditor in insert mode for the
// current selection.
export const extOpenNewRow = function extOpenNewRow(rootArg) {
  const el = _elFrom(this)
  const root = _externalRootFrom(el, rootArg)
  if (!root) return
  const { slug, table } = _parsePathString(root._extPath || '')
  if (!slug || !table) return
  root.update({ _extEditor: { mode: 'insert', slug, table, values: {}, original: null } })
}

// Whether the CURRENT selection accepts writes (drives the '+ New row'
// button): records plane always; instantdb when the namespace allowlist
// grants 'create'.
export const extSelectionEditable = function extSelectionEditable(rootArg) {
  const root = _externalRootFrom(_elFrom(this), rootArg)
  if (!root) return false
  const { slug, table } = _parsePathString(root._extPath || '')
  if (!slug || !table) return false
  if (slug === EXT_WORKSPACE_SLUG) return true
  const row = _findIntegration(root, slug)
  if (!row || row.kind !== 'instantdb' || !row.dispatchable) return false
  const schema = schemaForRemoteTable(root, slug, table)
  return Array.isArray(schema?.ops) ? schema.ops.includes('create') : false
}

// ---------- custom_http request runner ----------

export const extSetRunnerField = function extSetRunnerField(patch) {
  const root = _externalRootFrom(_elFrom(this), null)
  if (!root || !patch || typeof patch !== 'object') return
  root.update({ _extRunner: { ...(root._extRunner || {}), ...patch } })
}

export const extRunHttpRequest = async function extRunHttpRequest(rootArg) {
  const el = _elFrom(this)
  const root = _externalRootFrom(el, rootArg)
  if (!root) return
  const { slug } = _parsePathString(root._extPath || '')
  const row = _findIntegration(root, slug)
  if (!row || row.kind !== 'custom_http' || !row.dispatchable) return
  const runner = root._extRunner || {}
  if (runner.running) return
  let body
  const rawBody = typeof runner.body === 'string' ? runner.body.trim() : ''
  if (rawBody) {
    try {
      body = JSON.parse(rawBody)
    } catch {
      body = rawBody // non-JSON bodies pass through as text
    }
  }
  root.update({ _extRunner: { ...runner, running: true, error: null } })
  const method = String(runner.method || 'GET').toUpperCase()
  const res = await shellCallCapability(_capabilityRef(root, row), 'http.request', {
    method,
    path: runner.path || '/',
    ...(body !== undefined && method !== 'GET' && method !== 'HEAD' ? { body } : {})
  })
  const err = _readableCallError(el, res)
  root.update({
    _extRunner: {
      ...(root._extRunner || {}),
      running: false,
      error: err,
      status: res?.status ?? null,
      response: err && res?.result === undefined ? null : (res?.result ?? null)
    }
  })
}

// ---------- fetch-wiring snippets (the studio story) ----------

// Build the copyable snippet for the CURRENT selection →
// { label, code, caption } (never null — falls back to the sources list).
export const extSnippetFor = function extSnippetFor(rootArg) {
  const el = _elFrom(this)
  const root = _externalRootFrom(el, rootArg)
  const { slug, table } = _parsePathString(root?._extPath || '')
  const orgId = _orgId(root) || '<orgId>'
  const workspaceId = activeWorkspaceId(root) || '<workspaceId>'
  if (slug === EXT_WORKSPACE_SLUG && table) {
    return {
      label: el.call('polyglot', 'data.external.snippet.recordsLabel'),
      code:
        'fetch: [{\n' +
        "  from: 'workspaceProject.records',\n" +
        "  method: 'select',\n" +
        `  params: { collection: '${table}' },\n` +
        "  as: '~/rows'\n" +
        '}]',
      caption: el.call('polyglot', 'data.external.snippet.recordsCaption')
    }
  }
  if (slug && slug !== EXT_WORKSPACE_SLUG && root) {
    const row = _findIntegration(root, slug)
    if (row && row.kind === 'instantdb') {
      return {
        label: table
          ? el.call('polyglot', 'data.external.snippet.readTable').replace('{table}', table)
          : el.call('polyglot', 'data.external.snippet.callBackend'),
        code:
          "const res = await el.sdk.execute('orgIntegration', 'call', {\n" +
          `  orgId: '${orgId}',\n` +
          `  idOrSlug: '${slug}',\n` +
          "  capability: 'list',\n" +
          `  args: { namespace: '${table || '<namespace>'}', limit: 100 },\n` +
          `  workspaceId: '${workspaceId}'\n` +
          '})\n' +
          '// res → { ok, status, result: { rows, total } }',
        caption: el.call('polyglot', 'data.external.snippet.imperative')
      }
    }
    if (row && row.kind === 'custom_http') {
      return {
        label: el.call('polyglot', 'data.external.snippet.callBackend'),
        code:
          "const res = await el.sdk.execute('orgIntegration', 'call', {\n" +
          `  orgId: '${orgId}',\n` +
          `  idOrSlug: '${slug}',\n` +
          "  capability: 'http.request',\n" +
          "  args: { method: 'GET', path: '/status' },\n" +
          `  workspaceId: '${workspaceId}'\n` +
          '})\n' +
          '// res → { ok, status, result }',
        caption: el.call('polyglot', 'data.external.snippet.imperative')
      }
    }
  }
  return {
    label: el.call('polyglot', 'data.external.snippet.listLabel'),
    code:
      'fetch: [{\n' +
      "  from: 'orgIntegration',\n" +
      "  method: 'select',\n" +
      `  params: { orgId: '${orgId}', scopeType: 'workspace', scopeId: '${workspaceId}' },\n` +
      "  as: '~/integrations'\n" +
      '}]',
    caption: el
      .call('polyglot', 'data.external.snippet.listCaption')
      .replace('{imperative}', el.call('polyglot', 'data.external.snippet.imperative'))
  }
}

export const extCopySnippet = async function extCopySnippet() {
  const el = _elFrom(this)
  const root = _externalRootFrom(el, null)
  const snippet = extSnippetFor.call(el, root)
  if (!snippet?.code) return
  try {
    await navigator.clipboard.writeText(snippet.code)
    el?.call('openNotification', {
      title: el.call('polyglot', 'data.external.copy.done'),
      type: 'success'
    })
  } catch (e) {
    el?.call('openNotification', {
      title: el.call('polyglot', 'data.external.copy.failed'),
      message: e?.message,
      type: 'error'
    })
  }
}
